/**
 * Role normalizer.
 *
 * The Firebase Auth custom claim `role` can be one of four values
 * during the soft-migration window:
 *   - 'admin'  — canonical, unchanged
 *   - 'power'  — canonical, new (introduced with the RBAC overhaul)
 *   - 'user'   — canonical, new (replaces 'staff')
 *   - 'staff'  — legacy, treated as an alias for 'user' until the
 *                migration runs and converges every stored claim
 *
 * Every read path that reaches an actual role decision should pipe the
 * raw token claim through `normalizeRole()` so the rest of the app
 * only sees the canonical trio. New writes always use the canonical
 * literal (the setUserRole Cloud Function rejects `'staff'` as an
 * input). The compat shim here gets removed ~30 days after migration
 * completes.
 */

import type { UserRole } from '../types';

export type CanonicalRole = 'admin' | 'power' | 'user';

export const normalizeRole = (
  r: string | null | undefined,
): CanonicalRole | null => {
  if (r === 'admin' || r === 'power' || r === 'user') return r;
  if (r === 'staff') return 'user'; // compat alias — pre-migration claim
  return null;
};

/**
 * Loose role-coercion: takes anything (including the wider `UserRole`
 * type with the staff alias) and returns the canonical trio. Useful
 * inside components that received a typed `role: UserRole | null` from
 * useAuth() but want to switch on the post-normalized value without
 * having to redeclare the type.
 */
export const toCanonicalRole = (r: UserRole | null | undefined): CanonicalRole | null =>
  normalizeRole(r);
